import React, { useState, useRef, useEffect } from "react";

import { CindraButton } from "cindra/CindraButton.tsx";
import { MCPDashboard } from "mcp/components/MCPDashboard";
import { useMCP } from "mcp/hooks/useMCP";
import { useMCPAuth } from "mcp/hooks/useMCPAuth";

export const CindraDashboardPopover: React.FC = () => {
    const [showPopover, setShowPopover] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [buttonVisible, setButtonVisible] = useState(true);
    const [closeButtonVisible, setCloseButtonVisible] = useState(true);
    const buttonRef = useRef<HTMLDivElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    const { isCindraConnected, lastConnectionAttempt } = useMCP();
    const { isCindraEnabled, isCindraAuthenticated } = useMCPAuth();

    // Determine if Cindra is fully logged in
    const isCindraLoggedIn = isCindraEnabled && isCindraConnected && isCindraAuthenticated;

    const tooltipText = isCindraLoggedIn ? "Cindra MCP Client (Connected & Authenticated)" : "Cindra MCP Client (Disconnected)";

    // Choose icon based on state
    const cindraIcon = isCindraLoggedIn
        ? (
            // Connected/authenticated - lightning bolt icon
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
        )
        : (
            // Disconnected - disconnected/broken link icon
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
        );

    // Toggle popover with animation
    const handleButtonClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!showPopover) {
            // Start fading out button and show popover immediately (no delay)
            setButtonVisible(false);
            setShowPopover(true);
            setCloseButtonVisible(true);
            // Start slide-in animation immediately
            setTimeout(() => setIsAnimating(true), 10);
        }
    };

    // Close popover with animation
    const handleClosePopover = (e: React.MouseEvent) => {
        e.stopPropagation();
        // Hide close button immediately
        setCloseButtonVisible(false);
        // Start slide-out animation
        setIsAnimating(false);
        // Wait for panel animation to complete before hiding popover and showing button
        setTimeout(() => {
            setShowPopover(false);
            setButtonVisible(true);
        }, 500); // Match the panel slide-out duration
    };

    // Close popover when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                showPopover &&
                popoverRef.current &&
                !popoverRef.current.contains(event.target as Node) &&
                closeButtonRef.current &&
                !closeButtonRef.current.contains(event.target as Node)
            ) {
                handleClosePopover(event as any);
            }
        };

        if (showPopover) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showPopover]);

    // Update close button position to center vertically in header
    useEffect(() => {
        if (showPopover && headerRef.current && closeButtonRef.current) {
            const headerRect = headerRef.current.getBoundingClientRect();
            const centerY = headerRect.top + (headerRect.height / 2);

            closeButtonRef.current.style.top = `${centerY}px`;
        }
    }, [showPopover, isAnimating]);

    // Calculate popover position and animation
    const calculatePopoverStyle = (): React.CSSProperties => {
        return {
            position: 'fixed',
            top: '0px',
            right: '0px',
            width: 'auto',
            minWidth: '600px',
            maxHeight: 'calc(100vh - 40px)', // Account for sticky footer (approx 40px)
            transform: isAnimating ? 'translateX(0)' : 'translateX(100%)',
            transition: 'transform 500ms cubic-bezier(0.4, 0.0, 0.2, 1)', // Slower with ease-in-out
        };
    };

    const popoverStyle = calculatePopoverStyle();

    return (
        <>
            {/* Button with fade animation */}
            {!showPopover && (
                <CindraButton
                    ref={buttonRef}
                    style="normal"
                    color={isCindraLoggedIn ? "blue" : "yellow"}
                    icon={cindraIcon}
                    label="Cindra"
                    onClick={handleButtonClick}
                    title={tooltipText}
                    showLoadingState={false}
                    useFadeAnimation={true}
                    isVisible={buttonVisible}
                />
            )}
            {
                showPopover && (
                    <>
                        <div
                            ref={popoverRef}
                            className="fixed z-50 bg-white rounded-bl-lg shadow-2xl border-l-4 border-b-4 border-gray-400 flex flex-col"
                            style={{
                                ...popoverStyle,
                                boxShadow: '-8px 8px 24px rgba(0, 0, 0, 0.15), -4px 4px 12px rgba(0, 0, 0, 0.1)'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Popover Header */}
                            <div ref={headerRef} className="flex items-center justify-between px-4 py-3 border-b-2 border-gray-300 bg-blue-50 flex-shrink-0 shadow-md">
                                <h3 className="text-base font-semibold text-gray-900">Cindra Dashboard</h3>

                                {/* Connection Status - positioned further left of close button */}
                                <div className="flex flex-col items-end space-y-0.5 mr-16">
                                    <div className="flex items-center space-x-2">
                                        <div
                                            className={
                                                `w-3 h-3 rounded-full ${
                                                    isCindraConnected ? "bg-green-500" : "bg-red-500"
                                                }`
                                            }
                                        >
                                        </div>
                                        <span className="text-sm text-gray-600">
                                            {isCindraConnected ? "MCP Connected" : "MCP Disconnected"}
                                        </span>
                                    </div>
                                    {/* When Connected - Show Last Connection */}
                                    {
                                        isCindraConnected && lastConnectionAttempt && (
                                            <div className="text-xs text-gray-500">
                                                Last Connection: {lastConnectionAttempt.toLocaleTimeString()}
                                            </div>
                                        )
                                    }
                                </div>
                            </div>

                            {/* Popover Content - Scrollable, height fits content */}
                            <div className="overflow-y-auto overflow-x-hidden">
                                <MCPDashboard />
                            </div>
                        </div>

                        {/* Close button - aligned to right edge of viewport, centered vertically in header */}
                        {closeButtonVisible && (
                            <button
                                ref={closeButtonRef}
                                onClick={handleClosePopover}
                                className="fixed z-[60] text-gray-400 hover:text-gray-600 transition-colors bg-white rounded-full p-2 shadow-lg border border-gray-300"
                                style={{
                                    right: '16px', // 16px padding from right edge of viewport
                                    transform: 'translateY(-50%)', // Center vertically on the top value
                                    opacity: isAnimating ? 1 : 0,
                                    transition: 'opacity 500ms cubic-bezier(0.4, 0.0, 0.2, 1)',
                                }}
                                title="Close Dashboard"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </>
                )
            }
        </>
    );
};
